<template>
    <div class="p-4">
        <div class="flex flex-col md:flex-row md:items-end md:justify-between mb-6">
            <div>
                <h1 class="text-2xl font-semibold text-gray-900">Enterprise Alarm Report</h1>
                <p class="text-sm text-gray-500">Alarm activity across all regions and sites.</p>
            </div>
            <div class="flex flex-col md:flex-row md:items-center mt-4 md:mt-0">
                <div class="mb-3 md:mb-0 md:mr-4 w-full md:w-48">
                    <label class="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Start</label>
                    <date-time-picker id="enterpriseAlarmStart" v-model="startTime" format="MM/DD/YYYY"
                                      :max-date="startMaxDate" :min-date="startMinDate"
                                      placeholder="Start"></date-time-picker>
                </div>
                <div class="w-full md:w-48">
                    <label class="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">End</label>
                    <date-time-picker id="enterpriseAlarmEnd" v-model="endTime" format="MM/DD/YYYY"
                                      :max-date="endMaxDate" :min-date="endMinDate"
                                      placeholder="End"></date-time-picker>
                </div>
            </div>
        </div>

        <div v-if="loading" class="py-16 text-center text-gray-500">
            <span class="lnr icon-loading animate-spin mr-2"></span> Loading alarm data…
        </div>
        <div v-else-if="loadError" class="py-16 text-center text-red-600">
            {{ loadErrorMessage }}
        </div>

        <template v-else-if="report">
            <!-- Overall Summary -->
            <section class="mb-20">
                <h2 class="text-left text-xl font-semibold text-gray-800 border-b border-gray-200 pb-2 mb-4">Overall Summary</h2>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <region-alarm-status-pie-chart
                        :key="`overall-pie-${renderKey}`"
                        :data="overall.pieData"
                        chart-id="overall_alarm_pie"
                    ></region-alarm-status-pie-chart>
                    <region-alarm-count-table
                        :data="overall.countRows"
                        row-header="Region"
                    ></region-alarm-count-table>
                </div>
                <region-alarm-tracking-chart
                    :key="`overall-tracking-${renderKey}`"
                    :data="overall.trackingSeries"
                    chart-id="overall_alarm_tracking"
                ></region-alarm-tracking-chart>
            </section>

            <!-- Hard divider separating the overall summary from the per-region breakdown. -->
            <hr class="border-t-2 border-gray-300 mb-16" />

            <!-- Regions -->
            <section>
                <div class="sticky top-0 z-20 bg-white border-b border-gray-200 py-3 mb-4 flex flex-col sm:flex-row sm:items-center gap-3">
                    <h2 class="text-xl font-semibold text-gray-800">Regions</h2>
                    <div v-if="regions.length > 0" class="w-full sm:w-72 sm:ml-auto">
                        <select2
                            :key="`region-jump-${renderKey}`"
                            :allow-clear="true"
                            :close-on-select="true"
                            :options="regionJumpOptions"
                            :value="jumpTarget"
                            placeholder="Jump to region…"
                            width="100"
                            @update:value="jumpToRegion"
                        ></select2>
                    </div>
                </div>
                <div v-if="regions.length === 0" class="py-8 text-center text-gray-500">
                    No regions or sites found for this provider.
                </div>
                <div v-for="region in regions" :id="regionAnchorId(region.id)" :key="region.id" class="mb-12 scroll-mt-24">
                    <h3
                        :ref="(el) => observeRegionHeader(el, region.id)"
                        class="text-xl font-bold text-gray-900 text-left mb-4 pl-3 border-l-4 border-blue-600"
                    >{{ region.name }}</h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <region-alarm-status-pie-chart
                            :key="`region-pie-${renderKey}-${region.id}`"
                            :chart-id="`region_${region.id}_pie`"
                            :data="region.pieData"
                        ></region-alarm-status-pie-chart>
                        <region-alarm-count-table
                            :data="region.countRows"
                            row-header="Location"
                        ></region-alarm-count-table>
                    </div>
                    <div class="mb-6">
                        <region-alarm-tracking-chart
                            :key="`region-tracking-${renderKey}-${region.id}`"
                            :chart-id="`region_${region.id}_tracking`"
                            :data="region.trackingSeries"
                        ></region-alarm-tracking-chart>
                    </div>
                    <region-alarm-table
                        v-if="activatedRegions[region.id]"
                        :company-id="companyId"
                        :end="endParam"
                        :region-id="region.id"
                        :start="startParam"
                    ></region-alarm-table>
                    <div
                        v-else
                        class="mt-3 py-10 text-center text-sm text-gray-400"
                        style="min-height: 8rem;"
                    >
                        Alarms load when this region scrolls into view…
                    </div>
                </div>
            </section>
        </template>
    </div>
</template>

<script>
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {iconNamesMixin} from "@/mixins/IconNamesMixin";
import {DateTime} from "luxon";
import axios from "axios";
import DateTimePicker from "@/components/Reports/VueDatePicker";
import RegionAlarmStatusPieChart from "@/components/EnterpriseAlarmDashboard/RegionAlarmStatusPieChart.vue";
import RegionAlarmCountTable from "@/components/EnterpriseAlarmDashboard/RegionAlarmCountTable.vue";
import RegionAlarmTrackingChart from "@/components/EnterpriseAlarmDashboard/RegionAlarmTrackingChart.vue";
import RegionAlarmTable from "@/components/EnterpriseAlarmDashboard/RegionAlarmTable.vue";
import Select2 from "@/components/elements/select2.vue";

// Colors for the pie chart categories, kept in sync with DashboardPointStatusSummary's
// palette (red = out of range, grey = offline, yellow = caution) so the app reads as one system.
const STATUS_COLORS = {
    threshold: "#c53030",
    offline: "#a0aec0",
    snoozed: "#f6e05e",
};

// Cap the selectable window. This dashboard fans out alarm queries across every region and site
// under a provider, so an unbounded range is prohibitively expensive; 90 days is the widest window
// we're willing to serve. Enforced by the pickers' min/max-date bounds below.
const MAX_RANGE_DAYS = 90;

export default {
    name: "EnterpriseAlarmDashboard",
    mixins: [SonicuCoreMixin, iconNamesMixin],
    components: {
        RegionAlarmTable,
        RegionAlarmTrackingChart, RegionAlarmCountTable, RegionAlarmStatusPieChart, DateTimePicker, Select2},
    props: {
        companyId: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            // Default to the last 30 days.
            startTime: DateTime.now().minus({days: 30}).toJSDate(),
            endTime: new Date(),
            report: null,
            loading: false,
            loadError: false,
            loadErrorMessage: "Failed to load alarm data. Please try again.",
            // Bumped on each successful load so amCharts children remount cleanly.
            renderKey: 0,
            fetchTimeout: null,
            // Region ids whose RegionAlarmTable has been lazily activated (its header scrolled
            // near the viewport). Gates the table's v-if so its region-alarms request fires only
            // when the region is actually approached — not 90+ at once on page load.
            activatedRegions: {},
            // Bound to the "Jump to region" select2; reset to "" after each jump so the same region
            // can be re-selected.
            jumpTarget: "",
        };
    },
    mounted() {
        this.fetchReport();
        // Create the lazy-load observer up front. Each region header registers itself via its
        // :ref (observeRegionHeader) the moment it mounts, which sidesteps the DOM-timing race of
        // querying for headers before `loading` has flipped and the regions have rendered.
        if (typeof IntersectionObserver !== "undefined") {
            this.regionObserver = new IntersectionObserver(this.onRegionHeaderIntersect, {
                root: null,
                // Fire when a region header is still ~300px below the fold so its table has a head
                // start and is usually loaded by the time the user reaches it.
                rootMargin: "300px 0px",
                threshold: 0,
            });
        }
    },
    beforeUnmount() {
        if (this.regionObserver) {
            this.regionObserver.disconnect();
            this.regionObserver = null;
        }
    },
    computed: {
        overall() {
            const overall = this.report?.overall;
            if (!overall) {
                return {pieData: [], countRows: [], trackingSeries: []};
            }
            return {
                pieData: this.buildPieData(overall.statusCounts),
                countRows: overall.countRows,
                trackingSeries: overall.trend,
            };
        },
        regions() {
            // The per-alarm rows are no longer in this payload — each RegionAlarmTable fetches
            // its own paginated page from the region-alarms endpoint.
            return (this.report?.regions ?? []).map((region) => ({
                id: region.id,
                name: region.name,
                pieData: this.buildPieData(region.statusCounts),
                countRows: region.countRows,
                trackingSeries: region.trend,
            }));
        },
        // Date-picker bounds that keep the selected window within MAX_RANGE_DAYS (and out of the
        // future). Each bound is relative to the *other* field's current value, so picking either
        // end can never open a span wider than the cap.
        startMinDate() {
            return DateTime.fromJSDate(this.endTime).minus({days: MAX_RANGE_DAYS}).toJSDate();
        },
        startMaxDate() {
            // No later than the end, and never in the future.
            const now = new Date();
            return this.endTime < now ? this.endTime : now;
        },
        endMinDate() {
            return this.startTime;
        },
        endMaxDate() {
            // No more than MAX_RANGE_DAYS past the start, and never in the future.
            const cap = DateTime.fromJSDate(this.startTime).plus({days: MAX_RANGE_DAYS}).toJSDate();
            const now = new Date();
            return cap < now ? cap : now;
        },
        // yyyy-MM-dd strings handed to each RegionAlarmTable so it fetches the same window.
        startParam() {
            return this.formatDate(this.startTime);
        },
        endParam() {
            return this.formatDate(this.endTime);
        },
        // Identity of the current region SET (not the date range). Changes only when the regions
        // themselves change (new provider / first load) — which is when the lazy-load observer
        // must be reset and reattached. A mere date-range change keeps the same ids, so already
        // mounted tables just refetch via their own watchers and off-screen ones stay lazy.
        regionKey() {
            return this.regions.map((region) => region.id).join(",");
        },
        // { regionId: label } for the jump dropdown, ordered busiest-first (most active alarms) so
        // the regions that matter are at the top of the list; select2 keeps this insertion order.
        regionJumpOptions() {
            const regions = (this.report?.regions ?? [])
                .map((region) => ({
                    id: region.id,
                    name: region.name,
                    active: region.statusCounts?.active ?? 0,
                }))
                .sort((a, b) => b.active - a.active);

            const options = {};
            regions.forEach((region) => {
                options[region.id] = region.active > 0
                    ? `${region.name} (${region.active} active)`
                    : region.name;
            });

            return options;
        },
    },
    watch: {
        startTime() {
            this.scheduleFetch();
        },
        endTime() {
            this.scheduleFetch();
        },
        regionKey() {
            // The region set changed (new provider / first load): forget prior activations so the
            // new regions start lazy again. Their headers re-register via :ref as they render.
            this.activatedRegions = {};
        },
    },
    methods: {
        buildPieData(statusCounts) {
            const counts = statusCounts ?? {};
            return [
                {status: "Threshold", count: counts.threshold ?? 0, color: STATUS_COLORS.threshold},
                {status: "Offline", count: counts.offline ?? 0, color: STATUS_COLORS.offline},
                {status: "Snoozed", count: counts.snoozed ?? 0, color: STATUS_COLORS.snoozed},
            ];
        },
        scheduleFetch() {
            // Debounce rapid date-picker changes into a single request.
            clearTimeout(this.fetchTimeout);
            this.fetchTimeout = setTimeout(() => this.fetchReport(), 400);
        },
        fetchReport() {
            this.loading = true;
            this.loadError = false;

            const url = this.company.buildCompanyApiInternalRoute("enterprise-alarm-dashboard", this.companyId);

            axios
                .get(url, {
                    params: {
                        start: this.formatDate(this.startTime),
                        end: this.formatDate(this.endTime),
                    },
                })
                .then((response) => {
                    this.report = response.data;
                    this.renderKey += 1;
                })
                .catch((error) => {
                    this.loadError = true;
                    this.loadErrorMessage = error.response?.data?.error
                        ?? "Failed to load alarm data. Please try again.";
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        formatDate(date) {
            return DateTime.fromJSDate(date).toFormat("yyyy-MM-dd");
        },
        regionAnchorId(regionId) {
            return `enterprise-region-${regionId}`;
        },
        jumpToRegion(regionId) {
            // select2 also emits on (re)build and on clear; ignore anything but a real pick.
            if (!regionId) {
                return;
            }
            // Activate the target now in case it's far down and still lazy, then scroll to it. The
            // anchor carries scroll-mt so the sticky bar doesn't cover the region header.
            this.activatedRegions[regionId] = true;
            this.$nextTick(() => {
                // `auto` (instant jump) rather than `smooth`: a smooth scroll to a far region
                // sweeps every intermediate region header through the observer's rootMargin on the
                // way down, activating (and firing a region-alarms request for) all of them at once.
                document.getElementById(this.regionAnchorId(regionId))
                    ?.scrollIntoView({behavior: "auto", block: "start"});
                // Reset so picking the same region again re-triggers the jump.
                this.jumpTarget = "";
            });
        },
        observeRegionHeader(el, id) {
            // Called by each region header's :ref on mount (el is the element) and unmount (null).
            if (!el || this.activatedRegions[id]) {
                return;
            }
            // No IntersectionObserver support (old test runner / SSR): fall back to eager
            // activation per region so the tables still render.
            if (!this.regionObserver) {
                this.activatedRegions[id] = true;
                return;
            }
            el.dataset.regionId = id;
            this.regionObserver.observe(el);
        },
        onRegionHeaderIntersect(entries) {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) {
                    return;
                }
                const id = entry.target.dataset.regionId;
                if (id) {
                    // Mounts this region's RegionAlarmTable, which fetches its alarms on mount.
                    this.activatedRegions[id] = true;
                }
                // Activated once, never needs observing again.
                this.regionObserver.unobserve(entry.target);
            });
        },
    },
};
</script>

<style scoped>
</style>
